import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

export type BvUpsellRegion = 'US' | 'IN' | 'AE';
export type BvUpsellPlanType = 'subscription' | 'one_time_test';
export type BvPincodeBehavior =
  | 'us_price_and_availability'
  | 'in_availability_only';
export type BvUsVariantSet = 'rotus' | 'ny_nj';

export type BvUsPlanKey =
  | 'fasting_insulin'
  | 'hormone_health_female'
  | 'hormone_health_male';

export interface BvUpsellResolverOptions {
  usVariantSet?: BvUsVariantSet;
}

export interface BvLearnMoreContent {
  title: string;
  subtitle: string;
  about: string;
  keyBiomarkers: Array<{
    title: string;
    description?: string;
  }>;
}

export interface BvUpsellCard {
  id: string;
  region: BvUpsellRegion;
  planType: BvUpsellPlanType;
  title: string;
  subtitle?: string;
  description: string;
  imageSrc: string;
  surfaceImageSrc: {
    shop: string;
    crossSellModal: string;
    productListingCart: string;
  };
  shopifyVariantId: string;
  usPlanKey?: BvUsPlanKey;
  fallbackPrice?: number;
  learnMoreContent: BvLearnMoreContent;
  pincodeBehavior: BvPincodeBehavior;
  tag?: string;
  cartListingTitle: string;
  cartListingSubtitle: string;
}

type SupportedBvStore = ShopifyStore.US | ShopifyStore.IN | ShopifyStore.AE;
type BvPlanImageKey =
  | 'fasting_insulin'
  | 'diabetes_risk'
  | 'base_plan'
  | 'hormone_health_female'
  | 'hormone_health_male';

const BV_CART_LINE_PREFIX = 'Ultrahuman Blood Vision - ';
/** Staging Shopify variant IDs (shared shop; same across US variant sets and regions). */
const BV_STAGING_VARIANT_IDS = {
  fasting_insulin: '55666727125065',
  hormone_health_female: '55666727157833',
  hormone_health_male: '55666727190601',
  hair_health: '55666727223369',
} as const;
const BV_SHOP_IMAGE_BASE_PATH = 'web_v2/shop/blood-vision';
const BV_CROSS_SELL_MODAL_IMAGE_BASE_PATH = 'upsell/v4/desktop/bv';

const bvShopPlanImageFileByKey: Record<BvPlanImageKey, string> = {
  fasting_insulin: 'fasting-insulin-1.png',
  diabetes_risk: 'diabetes-risk-1.png',
  base_plan: 'base-plan-1.png',
  hormone_health_female: 'hormone-health-female-1.png',
  hormone_health_male: 'hormone-health-male-1.png',
};
const bvCrossSellModalImageFileByKey: Record<BvPlanImageKey, string> = {
  fasting_insulin: 'fasting-insulin-1.png',
  diabetes_risk: 'diabetes-risk-1.png',
  base_plan: 'base-plan-1.png',
  hormone_health_female: 'hormone-health-female-1.png',
  hormone_health_male: 'hormone-health-male-1.png',
};

const CART_ADDON_BV_BASE = 'web_v2/cart/addons/bv';

const bvCartAddonImageFileByKey: Record<BvPlanImageKey, string> = {
  fasting_insulin: 'insulin.png',
  diabetes_risk: 'diabetes.png',
  base_plan: 'base-plan.png',
  hormone_health_female: 'hormone-female.png',
  hormone_health_male: 'hormone-male.png',
};

const getBvSurfaceImageSrc = (key: BvPlanImageKey) => {
  const shopFile = bvShopPlanImageFileByKey[key];
  const crossSellFile = bvCrossSellModalImageFileByKey[key];
  return {
    shop: `${BV_SHOP_IMAGE_BASE_PATH}/${shopFile}`,
    crossSellModal: `${BV_CROSS_SELL_MODAL_IMAGE_BASE_PATH}/${crossSellFile}`,
    productListingCart: `${CART_ADDON_BV_BASE}/${bvCartAddonImageFileByKey[key]}`,
  };
};

const BV_CART_ADDON_SUBTITLES = {
  hormone_health_male: 'Track testosterone, DHT & male fertility hormones.',
  hormone_health_female: 'Track estradiol, FSH & female fertility hormones.',
  fasting_insulin: 'Spot insulin resistance before glucose does.',
  diabetes_risk: 'Glucose, insulin & HbA1c. Your diabetes-risk view.',
  base_plan: '60+ blood markers. Track health changes over time.',
} as const;

const basePlanLearnMore: BvLearnMoreContent = {
  title: 'Ultrahuman Base Plan',
  subtitle: '60+ biomarkers',
  about:
    'Essential blood panel covering 60+ markers across metabolic, hormonal, nutritional, and organ health, designed for regular monitoring at an accessible price.',
  keyBiomarkers: [
    {
      title: 'Glucose regulation',
      description: 'Fasting glucose, fasting insulin, HbA1c, and HOMA-IR.',
    },
    {
      title: 'Cardiovascular and lipid profile',
      description: 'Total cholesterol, triglycerides, HDL, LDL, and VLDL.',
    },
    {
      title: 'Micronutrients and inflammation',
      description:
        'Iron, ferritin, vitamins B9/B12/D, calcium, magnesium, hs-CRP, and homocysteine.',
    },
  ],
};

const fastingInsulinLearnMore: BvLearnMoreContent = {
  title: 'Fasting Insulin',
  subtitle: '1 marker',
  about:
    'A focused single-marker blood test to track fasting insulin and detect early metabolic drift before broader markers become abnormal.',
  keyBiomarkers: [
    {
      title: 'Fasting insulin',
      description:
        'Useful for tracking insulin sensitivity trends and interpreting metabolic response over time.',
    },
  ],
};

const diabetesRiskLearnMore: BvLearnMoreContent = {
  title: 'Diabetes Risk',
  subtitle: '4 markers',
  about:
    'A focused blood panel with key glucose-regulation markers to identify insulin resistance trends and diabetes risk early.',
  keyBiomarkers: [
    {
      title: 'Fasting glucose and fasting insulin',
      description:
        'Together these establish insulin sensitivity and metabolic baseline.',
    },
    {
      title: 'HbA1c and HOMA-IR',
      description:
        'Assess longer-term glycemic exposure and insulin resistance trajectory.',
    },
  ],
};

const hormoneHealthFemaleLearnMore: BvLearnMoreContent = {
  title: 'Female Hormone Health',
  subtitle: '5 Health and Longevity Markers • Female',
  about:
    "Female Hormone Health is a focused panel that maps the five hormones most central to a woman's reproductive cycle, fertility, mood, and long-term well-being. From cycle regularity and ovulatory health to perimenopausal transitions and energy balance, this test provides a clear hormonal baseline that can guide lifestyle adjustments, supplementation, and clinical conversations. It's particularly useful for women navigating symptoms like irregular periods, fatigue, or mood shifts, those actively planning conception, and anyone who wants a recurring, science-backed read on their hormonal rhythm.",
  keyBiomarkers: [
    {
      title: 'Core Metrics - 5 Parameters',
      description:
        'Follicle Stimulating Hormone, Lutenizing Hormone, Estradiol, Progesterone, DHEA-S',
    },
  ],
};

const hormoneHealthMaleLearnMore: BvLearnMoreContent = {
  title: 'Male Hormone Health',
  subtitle: '5 Health and Longevity Markers • Male',
  about:
    "Male Hormone Health is a focused panel that captures the five hormonal signals most critical to male vitality — energy, mood, libido, muscle and fat composition, and recovery.\n\nTotal and free testosterone, estradiol balance, DHEA-S, and a Prostate Specific Antigen reading together paint a complete picture of how the male endocrine system is functioning.\n\nIt's an ideal starting point for men considering testosterone optimization, troubleshooting persistent fatigue or low drive, monitoring prostate health as they age, or simply establishing a hormonal baseline to track over time.",
  keyBiomarkers: [
    {
      title: 'Core Metrics - 5 Parameters',
      description:
        'Testosterone (Total), Testosterone (Free), Estradiol, DHEA-S, Prostate Specific Antigen',
    },
  ],
};

type BvUsPlanBase = {
  key: BvUsPlanKey;
  id: string;
  title: string;
  subtitle: string;
  description: string;
  learnMoreContent: BvLearnMoreContent;
};

type BvUsVariantOverride = {
  fallbackPrice: number;
  shopifyVariantId: string;
};

const inUpsellFastingInsulinLearnMore: BvLearnMoreContent = {
  ...fastingInsulinLearnMore,
  about:
    'Catches insulin resistance up to a decade before glucose or HbA1c — the earliest warning signal of metabolic dysfunction.',
};

const inUpsellDiabetesRiskLearnMore: BvLearnMoreContent = {
  ...diabetesRiskLearnMore,
  about:
    'Fasting glucose, HbA1c, and insulin in one panel: a complete snapshot of your current diabetes risk.',
};

const inUpsellBasePlanLearnMore: BvLearnMoreContent = {
  ...basePlanLearnMore,
  about:
    '60+ essential blood markers for routine check-ups, so you can track how your health changes over time at an affordable price.',
};

const bloodVisionUpsellUsPlanBases: BvUsPlanBase[] = [
  {
    key: 'fasting_insulin',
    id: 'bv-us-fasting-insulin',
    title: 'Fasting Insulin (At-Lab)',
    subtitle: '1 marker',
    description:
      'Catches insulin resistance up to a decade before glucose or HbA1c — the earliest warning signal of metabolic dysfunction. ',
    learnMoreContent: fastingInsulinLearnMore,
  },
  {
    key: 'hormone_health_female',
    id: 'bv-us-hormone-health-female',
    title: 'Hormone Health Female (At-Lab)',
    subtitle: 'Comprehensive panel',
    description:
      'Hormonal markers that drive female fertility and reproductive health — built for women planning, trying, or troubleshooting conception.',
    learnMoreContent: hormoneHealthFemaleLearnMore,
  },
  {
    key: 'hormone_health_male',
    id: 'bv-us-hormone-health-male',
    title: 'Hormone Health Male (At-Lab)',
    subtitle: 'Comprehensive panel',
    description:
      'DHT, total and free testosterone in one panel: the key hormonal markers behind male fertility, vitality, and reproductive health. ',
    learnMoreContent: hormoneHealthMaleLearnMore,
  },
];

const bloodVisionUpsellUsVariantOverrides: Record<
  BvUsVariantSet,
  Record<BvUsPlanKey, BvUsVariantOverride>
> = {
  rotus: {
    fasting_insulin: {
      fallbackPrice: 3,
      shopifyVariantId: 'gid://shopify/ProductVariant/42724428709984',
    },
    hormone_health_female: {
      fallbackPrice: 55,
      shopifyVariantId: 'gid://shopify/ProductVariant/42724428742752',
    },
    hormone_health_male: {
      fallbackPrice: 49,
      shopifyVariantId: 'gid://shopify/ProductVariant/42724428775520',
    },
  },
  ny_nj: {
    fasting_insulin: {
      fallbackPrice: 15,
      shopifyVariantId: 'gid://shopify/ProductVariant/42724428841056',
    },
    hormone_health_female: {
      fallbackPrice: 129,
      shopifyVariantId: 'gid://shopify/ProductVariant/42724428873824',
    },
    hormone_health_male: {
      fallbackPrice: 75,
      shopifyVariantId: 'gid://shopify/ProductVariant/42724428906592',
    },
  },
};

const usBvStagingShopifyPlanRows: Record<
  BvUsVariantSet,
  Record<
    BvUsPlanKey,
    {
      title: string;
      description: string;
      markers: number;
      price: number;
      sku: string;
      variant_id: string;
      image_url: string;
    }
  >
> = {
  rotus: {
    fasting_insulin: {
      title: 'Insulin For All',
      description:
        'Insulin test for the Insulin For All campaign — affordable access to insulin testing.',
      markers: 1,
      price: 3,
      sku: 'UH-BV-US-RT-INS',
      variant_id: BV_STAGING_VARIANT_IDS.fasting_insulin,
      image_url:
        'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets-staging.uh-static.com/bv_onetime_insulin_for_all.png?speedsize=hh_2688,r_inside',
    },
    hormone_health_female: {
      title: 'Hormone Health Female (At-Lab)',
      description:
        'Hormonal markers that drive female fertility and reproductive health — built for women planning, trying, or troubleshooting conception.',
      markers: 7,
      price: 55,
      sku: 'UH-BV-US-RT-HRM-F',
      variant_id: BV_STAGING_VARIANT_IDS.hormone_health_female,
      image_url:
        'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets-staging.uh-static.com/bv_onetime_feminine_balance.png?speedsize=hh_2688,r_inside',
    },
    hormone_health_male: {
      title: 'Hormone Health Male (At-Lab)',
      description:
        'DHT, total and free testosterone in one panel: the key hormonal markers behind male fertility, vitality, and reproductive health. ',
      markers: 5,
      price: 49,
      sku: 'UH-BV-US-RT-HRM-M',
      variant_id: BV_STAGING_VARIANT_IDS.hormone_health_male,
      image_url:
        'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets-staging.uh-static.com/bv_onetime_t_optimize_pro.png?speedsize=hh_2688,r_inside',
    },
  },
  ny_nj: {
    fasting_insulin: {
      title: 'Insulin For All',
      description:
        'Insulin test for the Insulin For All campaign — affordable access to insulin testing.',
      markers: 1,
      price: 15,
      sku: 'UH-BV-US-NJ-INS',
      variant_id: BV_STAGING_VARIANT_IDS.fasting_insulin,
      image_url:
        'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets-staging.uh-static.com/bv_onetime_insulin_for_all.png?speedsize=hh_2688,r_inside',
    },
    hormone_health_female: {
      title: 'Hormone Health Female (At-Lab)',
      description:
        'Hormonal markers that drive female fertility and reproductive health — built for women planning, trying, or troubleshooting conception.',
      markers: 7,
      price: 129,
      sku: 'UH-BV-US-NJ-HRM-F',
      variant_id: BV_STAGING_VARIANT_IDS.hormone_health_female,
      image_url:
        'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets-staging.uh-static.com/bv_onetime_feminine_balance.png?speedsize=hh_2688,r_inside',
    },
    hormone_health_male: {
      title: 'Hormone Health Male (At-Lab)',
      description:
        'DHT, total and free testosterone in one panel: the key hormonal markers behind male fertility, vitality, and reproductive health. ',
      markers: 5,
      price: 69,
      sku: 'UH-BV-US-NJ-HRM-M',
      variant_id: BV_STAGING_VARIANT_IDS.hormone_health_male,
      image_url:
        'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets-staging.uh-static.com/bv_onetime_t_optimize_pro.png?speedsize=hh_2688,r_inside',
    },
  },
};

const buildBloodVisionUpsellCardsUSProduction = (
  usVariantSet: BvUsVariantSet = 'rotus',
): BvUpsellCard[] =>
  bloodVisionUpsellUsPlanBases.map((planBase) => {
    const variantOverride =
      bloodVisionUpsellUsVariantOverrides[usVariantSet][planBase.key];
    const preserveLearnMoreAboutForUsPlan =
      planBase.key === 'hormone_health_female' ||
      planBase.key === 'hormone_health_male'
        ? planBase.learnMoreContent.about
        : planBase.description;
    const surfaceImageSrc = getBvSurfaceImageSrc(planBase.key);
    return {
      id: planBase.id,
      region: 'US',
      planType: 'one_time_test',
      title: planBase.title,
      subtitle: planBase.subtitle,
      description: planBase.description,
      imageSrc: surfaceImageSrc.shop,
      surfaceImageSrc,
      shopifyVariantId: variantOverride.shopifyVariantId,
      usPlanKey: planBase.key,
      fallbackPrice: variantOverride.fallbackPrice,
      learnMoreContent: {
        ...planBase.learnMoreContent,
        about: preserveLearnMoreAboutForUsPlan,
      },
      pincodeBehavior: 'us_price_and_availability',
      cartListingTitle: `${BV_CART_LINE_PREFIX}${planBase.title}`,
      cartListingSubtitle: BV_CART_ADDON_SUBTITLES[planBase.key],
    };
  });

const buildBloodVisionUpsellCardsUSStaging = (
  usVariantSet: BvUsVariantSet = 'rotus',
): BvUpsellCard[] =>
  bloodVisionUpsellUsPlanBases.map((planBase) => {
    const row = usBvStagingShopifyPlanRows[usVariantSet][planBase.key];
    const markersLabel = `${row.markers} markers`;
    const surfaceImageSrc = getBvSurfaceImageSrc(planBase.key);
    return {
      id: planBase.id,
      region: 'US',
      planType: 'one_time_test',
      title: row.title,
      subtitle: markersLabel,
      description: row.description,
      imageSrc: surfaceImageSrc.shop,
      surfaceImageSrc,
      shopifyVariantId: `gid://shopify/ProductVariant/${row.variant_id}`,
      usPlanKey: planBase.key,
      fallbackPrice: row.price,
      learnMoreContent: {
        ...planBase.learnMoreContent,
        title: row.title,
        subtitle: markersLabel,
        about: row.description,
      },
      pincodeBehavior: 'us_price_and_availability',
      cartListingTitle: `${BV_CART_LINE_PREFIX}${planBase.title}`,
      cartListingSubtitle: BV_CART_ADDON_SUBTITLES[planBase.key],
    };
  });

const bloodVisionUpsellCardsINProduction: BvUpsellCard[] = [
  {
    id: 'bv-in-fasting-insulin',
    region: 'IN',
    planType: 'one_time_test',
    title: 'Fasting Insulin',
    subtitle: '1 marker',
    description:
      'Catches insulin resistance up to a decade before glucose or HbA1c — the earliest warning signal of metabolic dysfunction.',
    imageSrc: getBvSurfaceImageSrc('fasting_insulin').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('fasting_insulin'),
    shopifyVariantId: 'gid://shopify/ProductVariant/44955483668550',
    fallbackPrice: 649,
    learnMoreContent: inUpsellFastingInsulinLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Fasting Insulin`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.fasting_insulin,
  },
  {
    id: 'bv-in-diabetes-risk',
    region: 'IN',
    planType: 'one_time_test',
    title: 'Diabetes Risk',
    subtitle: '4 markers',
    description:
      'Fasting glucose, HbA1c, and insulin in one panel: a complete snapshot of your current diabetes risk.',
    imageSrc: getBvSurfaceImageSrc('diabetes_risk').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('diabetes_risk'),
    shopifyVariantId: 'gid://shopify/ProductVariant/44955483734086',
    fallbackPrice: 1099,
    learnMoreContent: inUpsellDiabetesRiskLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Diabetes Risk`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.diabetes_risk,
  },
  {
    id: 'bv-in-base-plan',
    region: 'IN',
    planType: 'one_time_test',
    title: 'Ultrahuman Base Plan',
    subtitle: '60+ markers',
    description:
      '60+ essential blood markers for routine check-ups, so you can track how your health changes over time at an affordable price.',
    imageSrc: getBvSurfaceImageSrc('base_plan').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('base_plan'),
    shopifyVariantId: 'gid://shopify/ProductVariant/44955483799622',
    fallbackPrice: 2299,
    learnMoreContent: inUpsellBasePlanLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Ultrahuman Base Plan`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.base_plan,
  },
];

const bloodVisionUpsellCardsAEProduction: BvUpsellCard[] = [
  {
    id: 'bv-ae-fasting-insulin',
    region: 'AE',
    planType: 'one_time_test',
    title: 'Fasting Insulin',
    subtitle: '1 marker',
    description:
      'Catches insulin resistance up to a decade before glucose or HbA1c — the earliest warning signal of metabolic dysfunction.',
    imageSrc: getBvSurfaceImageSrc('fasting_insulin').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('fasting_insulin'),
    shopifyVariantId: 'gid://shopify/ProductVariant/62777769230707',
    fallbackPrice: 159,
    learnMoreContent: inUpsellFastingInsulinLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Fasting Insulin`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.fasting_insulin,
  },
  {
    id: 'bv-ae-base-plan',
    region: 'AE',
    planType: 'one_time_test',
    title: 'Base Plan',
    subtitle: '60+ markers',
    description:
      '60+ essential blood markers for routine check-ups, so you can track how your health changes over time at an affordable price.',
    imageSrc: getBvSurfaceImageSrc('base_plan').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('base_plan'),
    shopifyVariantId: 'gid://shopify/ProductVariant/62777769197939',
    fallbackPrice: 499,
    learnMoreContent: inUpsellBasePlanLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Ultrahuman Base Plan`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.base_plan,
  },
];

const bloodVisionUpsellCardsINStaging: BvUpsellCard[] = [
  {
    id: 'bv-in-fasting-insulin',
    region: 'IN',
    planType: 'one_time_test',
    title: 'Fasting Insulin',
    subtitle: '1 marker',
    description:
      'Catches insulin resistance up to a decade before glucose or HbA1c — the earliest warning signal of metabolic dysfunction.',
    imageSrc: getBvSurfaceImageSrc('fasting_insulin').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('fasting_insulin'),
    shopifyVariantId: `gid://shopify/ProductVariant/${BV_STAGING_VARIANT_IDS.fasting_insulin}`,
    fallbackPrice: 649,
    learnMoreContent: inUpsellFastingInsulinLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Fasting Insulin`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.fasting_insulin,
  },
  {
    id: 'bv-in-diabetes-risk',
    region: 'IN',
    planType: 'one_time_test',
    title: 'Diabetes Risk',
    subtitle: '4 markers',
    description:
      'Fasting glucose, HbA1c, and insulin in one panel: a complete snapshot of your current diabetes risk.',
    imageSrc: getBvSurfaceImageSrc('diabetes_risk').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('diabetes_risk'),
    shopifyVariantId: `gid://shopify/ProductVariant/${BV_STAGING_VARIANT_IDS.hair_health}`,
    fallbackPrice: 1099,
    learnMoreContent: inUpsellDiabetesRiskLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Diabetes Risk`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.diabetes_risk,
  },
  {
    id: 'bv-in-base-plan',
    region: 'IN',
    planType: 'one_time_test',
    title: 'Ultrahuman Base Plan',
    subtitle: '60+ markers',
    description:
      '60+ essential blood markers for routine check-ups, so you can track how your health changes over time at an affordable price.',
    imageSrc: getBvSurfaceImageSrc('base_plan').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('base_plan'),
    shopifyVariantId: `gid://shopify/ProductVariant/${BV_STAGING_VARIANT_IDS.hormone_health_female}`,
    fallbackPrice: 2299,
    learnMoreContent: inUpsellBasePlanLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Ultrahuman Base Plan`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.base_plan,
  },
];

const bloodVisionUpsellCardsAEStaging: BvUpsellCard[] = [
  {
    id: 'bv-ae-fasting-insulin',
    region: 'AE',
    planType: 'one_time_test',
    title: 'Fasting Insulin',
    subtitle: '1 marker',
    description:
      'Catches insulin resistance up to a decade before glucose or HbA1c — the earliest warning signal of metabolic dysfunction.',
    imageSrc: getBvSurfaceImageSrc('fasting_insulin').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('fasting_insulin'),
    shopifyVariantId: `gid://shopify/ProductVariant/${BV_STAGING_VARIANT_IDS.fasting_insulin}`,
    fallbackPrice: 159,
    learnMoreContent: inUpsellFastingInsulinLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Fasting Insulin`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.fasting_insulin,
  },
  {
    id: 'bv-ae-base-plan',
    region: 'AE',
    planType: 'one_time_test',
    title: 'Ultrahuman Base Plan',
    subtitle: '60+ markers',
    description:
      '60+ essential blood markers for routine check-ups, so you can track how your health changes over time at an affordable price',
    imageSrc: getBvSurfaceImageSrc('base_plan').shop,
    surfaceImageSrc: getBvSurfaceImageSrc('base_plan'),
    shopifyVariantId: `gid://shopify/ProductVariant/${BV_STAGING_VARIANT_IDS.hormone_health_female}`,
    fallbackPrice: 499,
    learnMoreContent: inUpsellBasePlanLearnMore,
    pincodeBehavior: 'in_availability_only',
    cartListingTitle: `${BV_CART_LINE_PREFIX}Ultrahuman Base Plan`,
    cartListingSubtitle: BV_CART_ADDON_SUBTITLES.base_plan,
  },
];

const isProduction = process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION';

export const getAllUsBvVariantGidsForPlanKey = (
  planKey: BvUsPlanKey,
): string[] => {
  const sets: BvUsVariantSet[] = ['rotus', 'ny_nj'];
  if (isProduction) {
    return sets.map(
      (s) => bloodVisionUpsellUsVariantOverrides[s][planKey].shopifyVariantId,
    );
  }
  return sets.map(
    (s) =>
      `gid://shopify/ProductVariant/${usBvStagingShopifyPlanRows[s][planKey].variant_id}`,
  );
};

const normalizeBvVariantId = (variantId: string): string => {
  const trimmed = variantId.trim();
  const gidMatch = trimmed.match(/ProductVariant\/(\d+)\s*$/);
  if (gidMatch) return gidMatch[1];
  if (/^\d+$/.test(trimmed)) return trimmed;
  return trimmed;
};

export const getUsBvVariantSetForVariantId = (
  variantId: string,
): BvUsVariantSet | null => {
  const target = normalizeBvVariantId(variantId);
  if (!target) return null;
  const sets: BvUsVariantSet[] = ['rotus', 'ny_nj'];
  for (const set of sets) {
    for (const planKey of bloodVisionUpsellUsPlanBases.map(
      (plan) => plan.key,
    )) {
      const candidate = isProduction
        ? bloodVisionUpsellUsVariantOverrides[set][planKey].shopifyVariantId
        : `gid://shopify/ProductVariant/${usBvStagingShopifyPlanRows[set][planKey].variant_id}`;
      if (normalizeBvVariantId(candidate) === target) {
        return set;
      }
    }
  }
  return null;
};

const getBloodVisionUpsellCardsUS = (
  usVariantSet: BvUsVariantSet = 'rotus',
): BvUpsellCard[] =>
  isProduction
    ? buildBloodVisionUpsellCardsUSProduction(usVariantSet)
    : buildBloodVisionUpsellCardsUSStaging(usVariantSet);
const bloodVisionUpsellCardsIN = isProduction
  ? bloodVisionUpsellCardsINProduction
  : bloodVisionUpsellCardsINStaging;
const bloodVisionUpsellCardsAE = isProduction
  ? bloodVisionUpsellCardsAEProduction
  : bloodVisionUpsellCardsAEStaging;

export const bloodVisionUpsellCardsByStore: Partial<
  Record<ShopifyStore, BvUpsellCard[]>
> = {
  [ShopifyStore.US]: getBloodVisionUpsellCardsUS(),
  [ShopifyStore.IN]: bloodVisionUpsellCardsIN,
  [ShopifyStore.AE]: bloodVisionUpsellCardsAE,
};

const resolveSupportedBvStore = (
  store?: ShopifyStore | null,
): SupportedBvStore | null => {
  if (!store) return null;
  if (
    store === ShopifyStore.US ||
    store === ShopifyStore.IN ||
    store === ShopifyStore.AE
  ) {
    return store;
  }
  return null;
};

export const getBloodVisionUpsellCards = (
  store?: ShopifyStore | null,
  options?: BvUpsellResolverOptions,
): BvUpsellCard[] => {
  const resolvedStore = resolveSupportedBvStore(store);
  if (!resolvedStore) return [];
  const resolvedUsVariantSet = options?.usVariantSet ?? 'rotus';
  switch (resolvedStore) {
    case ShopifyStore.US:
      return [...getBloodVisionUpsellCardsUS(resolvedUsVariantSet)];
    case ShopifyStore.IN:
      return [...bloodVisionUpsellCardsIN];
    case ShopifyStore.AE:
      return [...bloodVisionUpsellCardsAE];
    default:
      return [];
  }
};

export const isBloodVisionUpsellStore = (store?: ShopifyStore | null) =>
  !!resolveSupportedBvStore(store) &&
  getBloodVisionUpsellCards(store).length > 0;
